<script lang="ts">
export default {
  name: 'NumberInput',
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { addCommas } from '@/utilities/Number';

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    prefix?: string;
    suffix?: string;
    error?: boolean;
    errorMessage?: string;
    hint?: string;
    class?: string;
  }>(),
  {
    disabled: false,
    error: false,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null | undefined];
}>();

const displayValue = computed({
  get() {
    if (props.modelValue === undefined || props.modelValue === null) {
      return '';
    }
    return addCommas(props.modelValue);
  },
  set(val: string) {
    const stripped = typeof val === 'string' ? val.replaceAll(',', '') : val;
    const num = stripped === '' ? '' : Number(stripped);
    const result = num === '' || isNaN(num as number) ? '' : (num as number);
    emit('update:modelValue', result === '' ? '' : result);
  },
});

const inputClasses = computed(() => {
  const classes = ['tw:input tw:input-bordered tw:w-full tw:min-w-0'];
  if (props.error) classes.push('tw:input-error');
  if (props.disabled) classes.push('tw:input-disabled');
  if (props.class) classes.push(props.class);
  return classes.join(' ');
});

const wrapperClasses = computed(() => {
  const classes = ['tw:input tw:input-bordered tw:w-full tw:flex tw:items-center tw:gap-2'];
  if (props.error) classes.push('tw:input-error');
  if (props.disabled) classes.push('tw:input-disabled');
  if (props.class) classes.push(props.class);
  return classes.join(' ');
});

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  displayValue.value = target.value;
}
</script>

<template>
  <div
    class="tw:form-control tw:w-full"
    v-bind="$attrs">
    <label
      v-if="label"
      class="tw:label">
      <span class="tw:label-text tw:text-sm">{{ label }}</span>
    </label>
    <!-- daisyUI pattern: label.input wraps prefix + input.grow + suffix -->
    <label
      v-if="prefix || suffix"
      :class="wrapperClasses">
      <span
        v-if="prefix"
        class="tw:opacity-50">
        {{ prefix }}
      </span>
      <input
        class="tw:grow tw:bg-transparent tw:outline-none focus:tw:outline-none"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        type="text"
        inputmode="decimal"
        @input="handleInput" />
      <span
        v-if="suffix"
        class="tw:opacity-50">
        {{ suffix }}
      </span>
    </label>
    <input
      v-else
      :class="inputClasses"
      :value="displayValue"
      :placeholder="placeholder"
      :disabled="disabled"
      type="text"
      inputmode="decimal"
      @input="handleInput" />
    <label
      v-if="error && errorMessage"
      class="tw:label">
      <span class="tw:label-text-alt tw:text-error">{{ errorMessage }}</span>
    </label>
    <label
      v-else-if="hint"
      class="tw:label">
      <span class="tw:label-text-alt tw:text-gray-500">{{ hint }}</span>
    </label>
  </div>
</template>
